import { LIST_UNIFIED_FIELDS_GQL } from '@modules/ManageUnifiedFields/hooks/useFetchUnifiedFields';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { CmdHotKeyIcon } from '@modules/common/components/HotKeyIcons/CmdHotKeyIcon';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useHotkey } from '@modules/common/hooks';
import { FETCH_QUERY_BUILDER_GROUPS_GQL } from '@modules/common/hooks/useFetchQueryBuilderGroupOptions/constants';
import { useUpdateMetadata } from '@modules/integrationsV2/hooks/useUpdateMetadata';
import { KeyboardReturnRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Paper, Stack } from '@mui/material';
import { MetadataDataType } from 'generated/graphql';
import { DeleteUnifiedFieldConfirmDialogContent } from '../DeleteUnifiedFieldConfirmDialogContent';

interface EditUnifiedFieldDrawerFooterProps {
  onClose: () => void;
  name?: string;
  description?: string;
  type?: MetadataDataType;
  isHidden?: boolean;
  referencedKeys?: string[];
  metadataKey: string;
  onSubmit?: () => void;
}

export const EditUnifiedFieldDrawerFooter = ({
  onClose,
  name,
  description,
  type,
  isHidden,
  referencedKeys,
  metadataKey,
  onSubmit,
}: EditUnifiedFieldDrawerFooterProps) => {
  const { openModal, closeModal } = useMuiModalContext();

  const [updateMetadata, { isLoading: loading }] = useUpdateMetadata({
    refetchQueries: [LIST_UNIFIED_FIELDS_GQL, FETCH_QUERY_BUILDER_GROUPS_GQL],
  });

  const isEditDisabled =
    !referencedKeys ||
    referencedKeys?.length < 2 ||
    !name?.trim() ||
    !description?.trim() ||
    !type;

  const onDelete = () => {
    openModal({
      contentComponent: () => (
        <DeleteUnifiedFieldConfirmDialogContent
          onClose={closeModal}
          closeDrawer={onClose}
          name={name}
          metadataKey={metadataKey}
          onSubmit={onSubmit}
        />
      ),
      modalProps: {},
    });
  };

  const onSave = async () => {
    try {
      await updateMetadata({
        displayText: name,
        description,
        referencedKeys: referencedKeys?.length ? referencedKeys : undefined,
        key: metadataKey,
        type,
        isHidden,
      });
      onSubmit?.();
      onClose();
    } catch {}
  };

  useHotkey({
    hotkey: '$mod+enter',
    onHotkey: onSave,
    isHotKeyDisabled: isEditDisabled,
  });

  return (
    <Paper
      sx={{
        mt: 'auto',
        p: 1,
        bottom: 0,
        position: 'sticky',
      }}
    >
      <Stack
        direction="row"
        ml="auto"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Box>
          <Button variant="text" color="error" onClick={onDelete}>
            Delete
          </Button>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            endIcon={<EscHotKeyIcon />}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={onSave}
            loading={loading}
            disabled={isEditDisabled}
            endIcon={
              <CmdHotKeyIcon
                plusHotKeyNode={<KeyboardReturnRounded />}
                isDisabled={isEditDisabled}
              />
            }
          >
            Save
          </LoadingButton>
        </Stack>
      </Stack>
    </Paper>
  );
};
